import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { MessageSquare, Plus } from "lucide-react";
import api from "@/lib/api";
import { StatusBadge } from "@/components/shared/StatusBadge";
import { UrgentChip } from "@/components/client/UrgentMarker";
import { StackedList, Pagination } from "@/components/shared/StackedList";
import { PageHeader, FilterBar } from "@/components/shared/PageChrome";
import { ViewTabs, SearchInput, InfoBanner, useListParams, ViewLink } from "@/components/shared/ListChrome";
import { LoadingState, ErrorState, EmptyState } from "@/components/shared/States";
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell } from "@/components/ui/table";

const VIEWS = [{ value: "ALL", label: "All requests" }, { value: "OPEN", label: "Open" }, { value: "AWAITING_INPUT", label: "Awaiting your input" }, { value: "COMPLETED", label: "Completed" }, { value: "CANCELLED", label: "Cancelled" }];

export default function ClientRequests() {
  const { view, q, page, setView, setQ, setPage } = useListParams();
  const [data, setData] = useState(null);
  const [error, setError] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    api.get("/client/requests", { params: { view, q: q || undefined, page, page_size: 25 } }).then((r) => setData(r.data)).catch(() => setError(true));
  }, [view, q, page]);

  if (error) return <ErrorState testId="client-requests-error" />;

  return (
    <div data-testid="client-requests-page" className="space-y-5">
      <PageHeader title="Accommodation Requests" subtitle="Track the progress of your accommodation requests with Jigsaw."
        actions={<InfoBannerAction />} />

      <ViewTabs testId="client-requests-views" tabs={VIEWS} value={view} onChange={setView} counts={data?.counts} />
      <FilterBar testId="client-requests-filter-bar" summary={data ? `${data.total} request${data.total === 1 ? "" : "s"} match` : null}>
        <SearchInput testId="client-requests-search" value={q} onChange={setQ} placeholder="Search by reference or location…" />
      </FilterBar>

      {!data && <LoadingState testId="client-requests-loading" />}
      {data && data.items.length === 0 && (
        <EmptyState testId="client-requests-empty" title={data.total === 0 && view === "ALL" && !q ? "No requests yet" : "No requests match this view"} description={view === "ALL" && !q ? "Requests you raise with Jigsaw will appear here." : "Try another view or clear the search."} />
      )}
      {data && data.items.length > 0 && (
        <div className="space-y-3">
          <StackedList testId="client-requests-stacked" items={data.items} title={(r) => <span className="flex items-center gap-2">{r.reference}<UrgentChip urgent={r.client_urgent} testId={`client-request-urgent-${r.reference}-m`} /></span>} status={(r) => <StatusBadge status={r.status} />} onOpen={(r) => navigate(`/client/requests/${r.id}`)} openLabel="Open request"
            fields={[{ label: "Location", value: (r) => r.location_text, full: true }, { label: "Dates", value: (r) => `${r.arrival_date || "—"} → ${r.departure_date || "—"}`, mono: true, full: true }, { label: "Rooms / people", value: (r) => `${r.rooms_required} / ${r.guests_count}` }, { label: "Priority", value: (r) => r.priority }]} />
          <div className="hidden sm:block">
          <Table data-testid="client-requests-table">
            <TableHeader>
              <TableRow>
                <TableHead>Request ID</TableHead><TableHead>Raised</TableHead><TableHead>Location</TableHead><TableHead>People</TableHead><TableHead>Dates</TableHead><TableHead>Priority</TableHead><TableHead>Status</TableHead><TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {data.items.map((r) => (
                <TableRow key={r.id} data-testid={`client-request-row-${r.reference}`} className="cursor-pointer hover:bg-jigsaw-bg" onClick={() => navigate(`/client/requests/${r.id}`)}>
                  <TableCell className="font-mono-ref font-medium text-jigsaw-navy"><span className="flex items-center gap-2">{r.reference}<UrgentChip urgent={r.client_urgent} testId={`client-request-urgent-${r.reference}`} /></span></TableCell>
                  <TableCell className="whitespace-nowrap text-[13px] text-jigsaw-muted">{new Date(r.created_at).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" })}</TableCell>
                  <TableCell>{r.location_text || "—"}</TableCell>
                  <TableCell>{r.guests_count}</TableCell>
                  <TableCell className="font-mono-ref whitespace-nowrap text-[13px]">{r.arrival_date || "—"} &rarr; {r.departure_date || "—"}</TableCell>
                  <TableCell><StatusBadge status={r.priority} /></TableCell>
                  <TableCell><StatusBadge status={r.status} /></TableCell>
                  <TableCell className="text-right"><ViewLink testId={`client-request-view-${r.reference}`} onClick={() => navigate(`/client/requests/${r.id}`)} /></TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
          </div>
          <Pagination testId="client-requests-pagination" page={data.page} pageSize={data.page_size} total={data.total} onPage={setPage} />
        </div>
      )}

      <InfoBanner testId="client-requests-banner" icon={MessageSquare} title="Need to provide more information?" text="You can add details or respond to a request from Jigsaw at any time through your support thread." actionLabel="Contact Jigsaw" to="/client/support" />
    </div>
  );
}

function InfoBannerAction() {
  const navigate = useNavigate();
  return (
    <button type="button" data-testid="client-new-request-button" onClick={() => navigate("/client/requests/new")} className="inline-flex h-9 items-center gap-1.5 rounded-md bg-jigsaw-orange px-3.5 text-[13px] font-medium text-white hover:bg-jigsaw-orangeDark">
      <Plus className="h-4 w-4" /> New request
    </button>
  );
}
